import React, { useEffect, useRef } from 'react'
import { motion } from 'framer-motion';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { ScrollToPlugin } from 'gsap/ScrollToPlugin';
import { AppWrap } from '../../wrapper';
import{ images } from '../../constants';
import  './Header.scss';

gsap.registerPlugin(ScrollTrigger, ScrollToPlugin);

/* A constant that is being used to animate the circles. */
const scaleVariants = {
  whileInView: {
    scale: [0, 1],
    opacity: [0, 1],
    transition: {
      duration: 1,
      ease: 'easeInOut'
    }
  }
}

const Header = () => {
  const headerRef = useRef(null);
  const titleRef = useRef(null);
  const subtitleRef = useRef(null);
  const avatarRef = useRef(null);
  const techCirclesRef = useRef(null);
  const bgShapeRef = useRef(null);
  const pinTimelineRef = useRef(null);

  useEffect(() => {
    // GSAP Timeline for entrance animations
    const tl = gsap.timeline();
    
    // Set initial states - Asegurar que todo tenga valores por defecto
    gsap.set([titleRef.current, subtitleRef.current], { y: 100, opacity: 0 });
    gsap.set(avatarRef.current, { scale: 0, opacity: 0 });
    gsap.set('.tech-circle', { scale: 0, rotation: 180, opacity: 0 });
    gsap.set('.bg-shape-center', { scale: 0, opacity: 0 });
    gsap.set('.code-line', { opacity: 0, x: -50 });
    gsap.set('.code-text', { width: 0, overflow: 'hidden' }); // Start with hidden text for typing effect

    // Estados base para el pin (para que funcione al retroceder)
    gsap.set('.tech-circle', { x: 0, y: 0, rotation: 0, scale: 1, opacity: 1 });
    gsap.set('.code-display', { scale: 1 });
    gsap.set('.code-line', { opacity: 1 });
    gsap.set('.bg-shape-center', { scale: 1, opacity: 0.8, rotation: 0 });

    // Animate elements in sequence
    tl.to('.bg-shape-center', { scale: 1, opacity: 0.9, duration: 2, ease: "power2.out" })
      .to(titleRef.current, { y: 0, opacity: 1, duration: 1, ease: "power3.out" }, "-=1.5")
      .to(subtitleRef.current, { y: 0, opacity: 1, duration: 0.8, ease: "power3.out" }, "-=0.5")
      .to(avatarRef.current, { scale: 1, opacity: 1, duration: 1, ease: "back.out(1.7)" }, "-=0.8")
      
      // Code typewriter effect - line by line with typing
      .to('.code-line', { 
        opacity: 1, 
        x: 0, 
        duration: 0.3, 
        stagger: 0.3,
        ease: "power2.out",
        onStart: function() {
          // Hide all code text initially
          gsap.set('.code-text', { width: 0, overflow: 'hidden' });
        }
      }, "-=0.5")
      // Typewriter effect with width animation and character reveal
      .to('.code-text', { 
        width: 'auto',
        duration: 1.5, // Slower for more visible typing 
        stagger: 0.6, // More delay between lines
        ease: "power1.inOut", // Smoother easing
        onStart: function() {
          // Speed up cursor during typing
          const cursor = document.querySelector('.terminal-cursor');
          if (cursor) {
            cursor.style.animationDuration = '0.3s';
          }
        },
        onComplete: function() {
          // Restore normal cursor speed
          const cursor = document.querySelector('.terminal-cursor');
          if (cursor) {
            cursor.style.animationDuration = '1s';
          }
        }
      }, "-=0.8")
      
      .to('.tech-circle', { 
        scale: 1, 
        rotation: 0, 
        opacity: 1, 
        duration: 0.5, 
        stagger: 0.1,
        ease: "back.out(1.7)" 
      }, "-=0.5");

    // Floating animation for tech circles
    gsap.to('.tech-circle', {
      y: "random(-15, 15)", // Reduced range for subtlety
      rotation: "random(-10, 10)", // Reduced rotation
      duration: 4, // Slower movement
      repeat: -1,
      yoyo: true,
      ease: "sine.inOut",
      stagger: 0.4
    });

    // Background shape continuous animation - commented out to prevent interference with other elements
    /* gsap.to('.bg-shape-center', {
      scale: 1.1,
      duration: 8,
      repeat: -1,
      yoyo: true,
      ease: "sine.inOut"
    }); */

    // PINNED SCROLL SEQUENCE - Pin más compacto
    pinTimelineRef.current = gsap.timeline({
      scrollTrigger: {
        trigger: '#home',
        start: "top top",
        end: "+=1600vh", // Aumentado a 1600vh para que las animaciones duren mucho más tiempo
        scrub: 0.8,
        pin: '#home',
        pinSpacing: true,
        anticipatePin: 1,
        fastScrollEnd: true,
        preventOverlaps: true,
        refreshPriority: 1,
        onLeave: () => {
          // Solo restaurar elementos básicos, NO el contenido del código
          // Solo restaurar elementos básicos, NO el contenido del código
          gsap.set('.tech-circle', { x: 0, y: 0, rotation: 0, scale: 1, opacity: 1 });
          gsap.set('.code-display', { scale: 1 });
          gsap.set('.bg-shape-center', { scale: 1, opacity: 0.8, rotation: 0 });
        },
        onEnterBack: () => {
          // Solo restaurar elementos básicos, NO el contenido del código
          // Solo restaurar elementos básicos, NO el contenido del código
          gsap.set('.tech-circle', { x: 0, y: 0, rotation: 0, scale: 1, opacity: 1 });
          gsap.set('.code-display', { scale: 1 });
          gsap.set('.bg-shape-center', { scale: 1, opacity: 0.8, rotation: 0 });
        }
      }
    });

    // Animaciones bidireccionales usando fromTo para definir claramente inicial y final
    
    // FASE 1: Solo preparar (0% - 25%)
    pinTimelineRef.current
      .addLabel("phase1")
      // Pequeña animación inicial para los iconos (opcional)
      .to('.tech-circle', {
        scale: 1.1,
        duration: 2,
        stagger: 0.3,
        ease: "sine.inOut",
        yoyo: true,
        repeat: 1
      }, "phase1")
      
      // FASE 2: Loading Terminal + Efectos de Iconos SIMULTÁNEOS (25% - 50%)
      .addLabel("phase2", "+=0.5")
      // Ocultar contenido original
      .to('.code-content', {
        opacity: 0,
        duration: 0.4
      }, "phase2")
      
      // Cambiar el contenido a loading terminal
      .set('.code-content', {
        innerHTML: `
          <div class="terminal-loading-content">
            <div class="terminal-line">
              <span class="line-number">$</span>
              <span class="loading-text">Loading portfolio...</span>
            </div>
            <div class="terminal-line">
              <span class="line-number"> </span>
              <div class="progress-bar-terminal">
                <span class="progress-brackets">[</span>
                <div class="progress-bar-fill">
                  <div class="progress-fill-bar"></div>
                </div>
                <span class="progress-brackets">]</span>
                <span class="progress-percent">0%</span>
              </div>
            </div>
            <div class="terminal-line">
              <span class="line-number"> </span>
              <span class="loading-status">Initializing...</span>
            </div>
            <div class="terminal-line">
              <span class="line-number"> </span>
              <span class="terminal-cursor-loading">_</span>
            </div>
          </div>
        `
      }, "phase2+=0.4")
      
      // Mostrar el nuevo contenido
      .to('.code-content', {
        opacity: 1,
        duration: 0.4
      }, "phase2+=0.4")
      
      // Animar la barra de progreso - extendido para máxima duración
      .to('.progress-fill-bar', {
        width: '100%',
        duration: window.innerWidth <= 768 ? 16 : 32, // Más rápido en mobile
        ease: "power2.inOut"
      }, "phase2+=0.8")
      
      // Animar el porcentaje y cambiar el estado - sincronizado con la barra
      .to({}, {
        duration: window.innerWidth <= 768 ? 16 : 32, // Más rápido en mobile
        onUpdate: function() {
          const progress = Math.round(this.progress() * 100);
          const percentElement = document.querySelector('.progress-percent');
          const statusElement = document.querySelector('.loading-status');
          const progressBar = document.querySelector('.progress-fill-bar');
          
          if (percentElement) {
            percentElement.textContent = progress + '%';
          }
          
          // Asegurar que la barra visual se sincronice
          if (progressBar) {
            progressBar.style.width = progress + '%';
          }
          
          if (statusElement) {
            if (progress < 20) statusElement.textContent = 'Loading components...';
            else if (progress < 40) statusElement.textContent = 'Compiling assets...';
            else if (progress < 60) statusElement.textContent = 'Building interface...';
            else if (progress < 80) statusElement.textContent = 'Almost ready...';
            else if (progress < 100) statusElement.textContent = 'Finishing up...';
            else statusElement.textContent = 'Complete! ✓';
          }
        }
      }, "phase2+=0.8")
      
      // *** EFECTOS DE ICONOS SIMULTÁNEOS CON EL LOADING ***
      // ICONO 1: Agrandar y desaparecer (al inicio del loading)
      .to('.tech-circle:nth-child(1)', {
        scale: window.innerWidth <= 768 ? 2.2 : 5.25, // 15% menos crecimiento en mobile
        duration: window.innerWidth <= 768 ? 8 : 16, // Más tiempo para crecer
        ease: "back.out(1.7)"
      }, "phase2+=1") // Empezar casi inmediatamente
      .to('.tech-circle:nth-child(1)', {
        opacity: 0,
        scale: 0,
        duration: window.innerWidth <= 768 ? 4 : 8, // Más rápido en mobile
        ease: "power2.inOut"
      }, window.innerWidth <= 768 ? "phase2+=6" : "phase2+=12") // Terminar antes para dar espacio a Angular
      
      // ICONO 2: Agrandar y desaparecer (segundo tercio del loading)
      .to('.tech-circle:nth-child(2)', {
        scale: window.innerWidth <= 768 ? 2.2 : 5.25, // 15% menos crecimiento en mobile
        duration: window.innerWidth <= 768 ? 4 : 8, // Más rápido en mobile
        ease: "back.out(1.7)"
      }, window.innerWidth <= 768 ? "phase2+=4" : "phase2+=8") // Empezar después de React: 4s en mobile, 8s en desktop
      .to('.tech-circle:nth-child(2)', {
        opacity: 0,
        scale: 0,
        duration: window.innerWidth <= 768 ? 3 : 6, // Más rápido en mobile
        ease: "power2.inOut"
      }, window.innerWidth <= 768 ? "phase2+=8" : "phase2+=16") // Terminar antes de Node: 8s en mobile, 16s en desktop
      
      // ICONO 3: Agrandar y desaparecer (tercer tercio del loading)
      .to('.tech-circle:nth-child(3)', {
        scale: window.innerWidth <= 768 ? 2.2 : 5.25, // 15% menos crecimiento en mobile
        duration: window.innerWidth <= 768 ? 4 : 8, // Más rápido en mobile
        ease: "back.out(1.7)"
      }, window.innerWidth <= 768 ? "phase2+=9" : "phase2+=18") // Timing proporcional: 9s en mobile, 18s en desktop
      .to('.tech-circle:nth-child(3)', {
        opacity: 0,
        scale: 0,
        duration: window.innerWidth <= 768 ? 4 : 8, // Más rápido en mobile
        ease: "power2.inOut"
      }, window.innerWidth <= 768 ? "phase2+=13" : "phase2+=26") // Timing proporcional: 13s en mobile, 26s en desktop
      
      // FASE 3: Background - Sutil (50% - 75%) - más tiempo después del loading
      .addLabel("phase3", "+=1.5")
      .fromTo('.bg-shape-center',
        { scale: 1, opacity: 0.8, rotation: 0 }, // Estado inicial
        {
          scale: 1.2, // Menos dramático
          opacity: 0.6,
          rotation: 45, // Menos rotación
          duration: 2,
          ease: "power2.inOut"
        }, "phase3")
      
      // FASE 4: Iconos reaparecen y se mantienen (75% - 100%)
      .addLabel("phase4", "+=2")
      // Hacer que reaparezcan todos juntos
      .to('.tech-circle', {
        opacity: 1,
        scale: 1,
        duration: 1.5,
        stagger: 0.3,
        ease: "back.out(1.7)"
      }, "phase4")
      
      // Asegurar que el loading quede completo al 100%
      .set('.loading-status', {
        innerHTML: 'Loading complete! Ready to continue ✓'
      }, "phase4+=2")
      .set('.progress-percent', {
        innerHTML: '100%'
      }, "phase4+=2")
      .set('.progress-fill-bar', {
        width: '100%'
      }, "phase4+=2")
      
      .to('.bg-shape-center', {
        scale: 1,
        opacity: 0.8,
        rotation: 0,
        duration: 2,
        ease: "power2.inOut"
      }, "phase4+=0.5");

    // Simple reset trigger for navigation - NO aggressive scroll forcing
    ScrollTrigger.create({
      trigger: '#home',
      start: "top center",
      end: "top top",
      onEnter: () => {
        // Gentle reset only if we're close to the top
        const scrollY = window.scrollY;
        if (scrollY < 50 && pinTimelineRef.current) {
          console.log("🔄 Gentle reset for navigation...");
          
          // Simple timeline reset without breaking scroll
          pinTimelineRef.current.seek(0);
          
          // Restore original code content
          const codeContent = document.querySelector('.code-content');
          if (codeContent) {
            codeContent.innerHTML = `
              <div class="code-line" data-line="1">
                <span class="line-number">1</span>
                <span class="code-text">
                  <span class="keyword">const</span> <span class="variable">developer</span> <span class="operator">=</span> <span class="bracket">{</span>
                </span>
              </div>
              <div class="code-line" data-line="2">
                <span class="line-number">2</span>
                <span class="code-text">
                  &nbsp;&nbsp;<span class="property">name</span><span class="colon">:</span> <span class="string">'Aldo'</span>,
                </span>
              </div>
              <div class="code-line" data-line="3">
                <span class="line-number">3</span>
                <span class="code-text">
                  &nbsp;&nbsp;<span class="property">role</span><span class="colon">:</span> <span class="string">'Full Stack'</span>,
                </span>
              </div>
              <div class="code-line" data-line="4">
                <span class="line-number">4</span>
                <span class="code-text">
                  &nbsp;&nbsp;<span class="property">passion</span><span class="colon">:</span> <span class="string">'Creating awesome apps'</span>
                </span>
              </div>
              <div class="code-line" data-line="5">
                <span class="line-number">5</span>
                <span class="code-text">
                  <span class="bracket">}</span>
                </span>
              </div>
              <div class="terminal-cursor"></div>
            `;
          }
          
          // Reset progress bar
          gsap.set('.progress-bar', { scaleX: 0 });
          const hintElement = document.querySelector('.scroll-hint span');
          if (hintElement) {
            hintElement.textContent = "Scroll to explore";
          }

        }
      }
    });

    // El pin de GSAP se encarga de todo automáticamente

    // Parallax removido para mantener el título fijo
    // El título "Hey, I'm Aldo" ahora permanece estático durante las animaciones

    // Advanced Hover Effects with GSAP
    
    // Magnetic effect for tech icons
    document.querySelectorAll('.tech-circle').forEach(el => {
      el.addEventListener('mouseenter', (e) => {
        // Minimal scale on mobile to avoid covering code display
        const isMobile = window.innerWidth <= 768;
        const scale = isMobile ? 1.01 : 1.2;
        
        gsap.to(el, {
          scale: scale,
          rotation: 5,
          duration: 0.3,
          ease: "back.out(1.7)"
        });
      });
      
      el.addEventListener('mouseleave', (e) => {
        gsap.to(el, {
          scale: 1,
          rotation: 0,
          x: 0,
          y: 0,
          duration: 0.3,
          ease: "back.out(1.7)"
        });
      });

      // Magnetic follow effect
      el.addEventListener('mousemove', (e) => {
        const rect = el.getBoundingClientRect();
        const x = e.clientX - rect.left - rect.width / 2;
        const y = e.clientY - rect.top - rect.height / 2;
        
        gsap.to(el, {
          x: x * 0.3,
          y: y * 0.3,
          duration: 0.2,
          ease: "power2.out"
        });
      });
    });

    // Code display hover effect
    const codeDisplay = document.querySelector('.code-display');
    if (codeDisplay) {
      codeDisplay.addEventListener('mouseenter', () => {
        gsap.to('.code-line', {
          x: 10,
          duration: 0.3,
          stagger: 0.05,
          ease: "power2.out"
        });
        gsap.to('.terminal-cursor', {
          scaleX: 3,
          opacity: 1,
          duration: 0.2
        });
      });

      codeDisplay.addEventListener('mouseleave', () => {
        gsap.to('.code-line', {
          x: 0,
          duration: 0.3,
          stagger: 0.05,
          ease: "power2.out"
        });
        gsap.to('.terminal-cursor', {
          scaleX: 1,
          duration: 0.2
        });
      });
    }

    // Hero text interactive effects
    const heroTitle = titleRef.current;
    const heroSubtitle = subtitleRef.current;
    
    if (heroTitle) {
      heroTitle.addEventListener('mouseenter', () => {
        gsap.to('.hero-name', {
          scale: 1.1,
          textShadow: "0 0 20px rgba(123, 192, 255, 0.5)",
          duration: 0.3
        });
      });

      heroTitle.addEventListener('mouseleave', () => {
        gsap.to('.hero-name', {
          scale: 1,
          textShadow: "none",
          duration: 0.3
        });
      });
    }

    // Custom Magnetic Cursor
    const cursor = document.createElement('div');
    cursor.className = 'custom-cursor';
    document.body.appendChild(cursor);

    // Removed cursor follower circle - keeping only the main cursor

    let mouseX = 0, mouseY = 0;
    
    // Mouse tracking solo para cursors personalizados (gradiente global se maneja en App.js)
    document.addEventListener('mousemove', (e) => {
      mouseX = e.clientX;
      mouseY = e.clientY;
      
      // Cursor normal
      gsap.to(cursor, {
        x: mouseX,
        y: mouseY,
        duration: 0,
      });
      
      // Cursor follower removed
    });

    // Cursor interactions
    document.querySelectorAll('.tech-circle, .code-display, .hero-name').forEach(el => {
      el.addEventListener('mouseenter', () => {
        gsap.to(cursor, {
          scale: 2,
          duration: 0.2
        });
        // Cursor follower removed
        
        // Intensificar el gradiente global del mouse cuando está sobre elementos interactivos
        const globalMouseGradient = document.querySelector('.global-mouse-gradient-effect');
        if (globalMouseGradient) {
          gsap.to(globalMouseGradient, {
            opacity: 1,
            scale: 1.2,
            duration: 0.3,
            ease: "power2.out"
          });
        }
      });
      
      el.addEventListener('mouseleave', () => {
        gsap.to(cursor, {
          scale: 1,
          duration: 0.2
        });
        // Cursor follower removed
        
        // Volver a la intensidad normal del gradiente global
        const globalMouseGradient = document.querySelector('.global-mouse-gradient-effect');
        if (globalMouseGradient) {
          gsap.to(globalMouseGradient, {
            opacity: 0.8,
            scale: 1,
            duration: 0.3,
            ease: "power2.out"
          });
        }
      });
    });

    // Scroll Progress Indicator Animation - COMMENTED OUT for now
    /*
    ScrollTrigger.create({
      trigger: '#home',
      start: "top top",
      end: "+=1600vh", // Igual que el pin timeline
      scrub: 0.8, // Igual que el pin timeline
      onUpdate: (self) => {
        // Sincronizar con el progreso del timeline principal
        const timelineProgress = pinTimelineRef.current ? pinTimelineRef.current.progress() : self.progress;
        gsap.to('.progress-bar', {
          scaleX: timelineProgress,
          duration: 0.1,
          ease: "none"
        });
        
        // Ocultar el scroll-me-hint cuando se empiece a hacer scroll
        const scrollMeHint = document.querySelector('.scroll-me-hint');
        if (scrollMeHint && self.progress > 0.02) {
          gsap.to(scrollMeHint, {
            opacity: 0,
            y: 10,
            duration: 0.5,
            ease: "power2.out"
          });
        } else if (scrollMeHint && self.progress <= 0.02) {
          gsap.to(scrollMeHint, {
            opacity: 1,
            y: 0,
            duration: 0.5,
            ease: "power2.out"
          });
        }
      },
      onEnter: () => {
        // Al entrar al trigger, asegurar que empiece desde 0
        gsap.set('.progress-bar', { scaleX: 0 });
      },
      onLeave: () => {
        // Al salir del trigger, mantener al 100%
        gsap.set('.progress-bar', { scaleX: 1 });
      },
      onEnterBack: () => {
        // Al regresar, resetear completamente
        gsap.set('.progress-bar', { scaleX: 0 });
        
        // Mostrar el scroll-me-hint nuevamente
        const scrollMeHint = document.querySelector('.scroll-me-hint');
        if (scrollMeHint) {
          gsap.set(scrollMeHint, { opacity: 1, y: 0 });
        }
      }
    });
    */
    
    // Simple scroll hint visibility
    ScrollTrigger.create({
      trigger: '#home',
      start: "top top",
      end: "bottom top",
      onUpdate: (self) => {
        const scrollMeHint = document.querySelector('.scroll-me-hint');
        if (scrollMeHint && self.progress > 0.02) {
          gsap.to(scrollMeHint, {
            opacity: 0,
            y: 10,
            duration: 0.5,
            ease: "power2.out"
          });
        } else if (scrollMeHint && self.progress <= 0.02) {
          gsap.to(scrollMeHint, {
            opacity: 1,
            y: 0,
            duration: 0.5,
            ease: "power2.out"
          });
        }
      }
    });

    // Scroll hint animation
    gsap.to('.scroll-arrow', {
      y: 10,
      duration: 1.5,
      repeat: -1,
      yoyo: true,
      ease: "power2.inOut"
    });

    // Listener para detectar cuando estamos en el inicio absoluto y resetear
    ScrollTrigger.create({
      trigger: 'body',
      start: "top top", // Cuando estamos exactamente en el inicio
      end: "top top",
      onEnter: () => {
        // Solo resetear si venimos de otra sección (no durante carga inicial)
        if (pinTimelineRef.current && window.scrollY === 0) {
          console.log("🔄 Auto-reset detectado en pixel 0");
          
          // Reset del timeline 
          pinTimelineRef.current.seek(0);
          
          // Restaurar contenido original
          setTimeout(() => {
            const codeContent = document.querySelector('.code-content');
            if (codeContent) {
              codeContent.innerHTML = `
                <div class="code-line" data-line="1">
                  <span class="line-number">1</span>
                  <span class="code-text">
                    <span class="keyword">const</span> <span class="variable">developer</span> <span class="operator">=</span> <span class="bracket">{</span>
                  </span>
                </div>
                <div class="code-line" data-line="2">
                  <span class="line-number">2</span>
                  <span class="code-text">
                    &nbsp;&nbsp;<span class="property">name</span><span class="colon">:</span> <span class="string">'Aldo'</span>,
                  </span>
                </div>
                <div class="code-line" data-line="3">
                  <span class="line-number">3</span>
                  <span class="code-text">
                    &nbsp;&nbsp;<span class="property">role</span><span class="colon">:</span> <span class="string">'Full Stack'</span>,
                  </span>
                </div>
                <div class="code-line" data-line="4">
                  <span class="line-number">4</span>
                  <span class="code-text">
                    &nbsp;&nbsp;<span class="property">passion</span><span class="colon">:</span> <span class="string">'Creating awesome apps'</span>
                  </span>
                </div>
                <div class="code-line" data-line="5">
                  <span class="line-number">5</span>
                  <span class="code-text">
                    <span class="bracket">}</span>
                  </span>
                </div>
                <div class="terminal-cursor"></div>
              `;
            }
            
            // Reset UI elements
            gsap.set('.progress-bar', { scaleX: 0 });
            
            // Reset code elements positions
            gsap.set('.code-line', { x: 0, opacity: 1 });
            gsap.set('.code-display', { scale: 1 });
            
            // Mostrar el scroll-me-hint nuevamente
            const scrollMeHint = document.querySelector('.scroll-me-hint');
            if (scrollMeHint) {
              gsap.set(scrollMeHint, { opacity: 1, y: 0 });
            }
            
            console.log("✅ Auto-reset completado en pixel 0");
          }, 100);
        }
      },
      onEnterBack: () => {
        // También resetear cuando regresamos hacia arriba al pixel 0
        if (pinTimelineRef.current && window.scrollY === 0) {
          console.log("🔄 Auto-reset detectado al regresar a pixel 0");
          pinTimelineRef.current.seek(0);
          
          // Reset UI elements
          gsap.set('.progress-bar', { scaleX: 0 });
          
          // Mostrar el scroll-me-hint nuevamente
          const scrollMeHint = document.querySelector('.scroll-me-hint');
          if (scrollMeHint) {
            gsap.set(scrollMeHint, { opacity: 1, y: 0 });
          }
        }
      }
    });

    // Cleanup function
    return () => {
      if (cursor && cursor.parentNode) {
        cursor.parentNode.removeChild(cursor);
      }
      // Cursor follower removed
      // El mouseGradient se limpia automáticamente al desmontar el componente
    };

  }, []);

  return (
    <div className='app__header app__flex' ref={headerRef}>
      {/* Background decorative shapes */}
      <div className="header-bg-shapes">
        <div className="bg-shape bg-shape-center"></div>
      </div>

      <div className='app__header-content'>
        {/* Main Typography Section */}
        <div className='app__header-text'>
          <div className='hero-greeting' ref={titleRef}>
            <span className="wave-emoji">👋</span>
            <h1 className='hero-title'>
              Hey, I'm <span className='hero-name'>Aldo</span>
            </h1>
          </div>
          <h2 className='hero-subtitle' ref={subtitleRef}>
            A <span className='gradient-text'>Full Stack Developer</span>
          </h2>
          <p className='hero-description'>
            with solid foundations in design, 
            passionate about crafting seamless user experiences I thrive at the intersection 
            of creativity and functionality.
          </p>
        </div>

        {/* Code Display Section */}
        <div className='app__header-avatar' ref={avatarRef}>
          <div className='code-display'>
            <div className='code-header'>
              <div className='window-controls'>
                <span className='control red'></span>
                <span className='control yellow'></span>
                <span className='control green'></span>
              </div>
              <span className='file-name'>developer.jsx</span>
            </div>
            <div className='code-content'>
              <div className='code-line' data-line="1">
                <span className='line-number'>1</span>
                <span className='code-text'>
                  <span className='keyword'>const</span> <span className='variable'>developer</span> <span className='operator'>=</span> <span className='bracket'>{"{"}</span>
                </span>
              </div>
              <div className='code-line' data-line="2">
                <span className='line-number'>2</span>
                <span className='code-text'>
                  &nbsp;&nbsp;<span className='property'>name</span><span className='colon'>:</span> <span className='string'>'Aldo'</span>,
                </span>
              </div>
              <div className='code-line' data-line="3">
                <span className='line-number'>3</span>
                <span className='code-text'>
                  &nbsp;&nbsp;<span className='property'>role</span><span className='colon'>:</span> <span className='string'>'Full Stack'</span>,
                </span>
              </div>
              <div className='code-line' data-line="4">
                <span className='line-number'>4</span>
                <span className='code-text'>
                  &nbsp;&nbsp;<span className='property'>passion</span><span className='colon'>:</span> <span className='string'>'Creating awesome apps'</span>
                </span>
              </div>
              <div className='code-line' data-line="5">
                <span className='line-number'>5</span>
                <span className='code-text'>
                  <span className='bracket'>{"}"}</span>
                </span>
              </div>
              <div className='terminal-cursor'></div>
            </div>
          </div>
        </div>

        {/* Tech Stack Circles */}
        <div className='app__header-tech' ref={techCirclesRef}>
          {[images.react, images.angular, images.node].map((tech, index) => (
            <div className='tech-circle' key={`tech-${index}`}>
              <img src={tech} alt={`technology-${index}`} /> 
            </div>
          ))}
        </div>
      </div>

      {/* Scroll Progress Indicator */}
      <div className="scroll-progress">
        <div className="progress-bar"></div>
      </div>

      {/* Scroll Me Hint - Simple and Clean */}
      <div className="scroll-me-hint">
        <span className="scroll-me-text">Scroll Me!</span>
        <div className="scroll-me-arrow">▼</div>
      </div>

      {/* Mouse Gradient Effect now handled globally in App.js */}
    </div>
  )
}

export default AppWrap(Header, 'home');